import { BundleResultsGrid } from "../../../components/resultsGrid/BundleResultsGrid";
import { FluidVirtualGrid } from "../../../components/resultsGrid/FluidVirtualGrid";
import { ResultsGrid } from "../../../components/resultsGrid/ResultsGrid";
import { sizeEstimator } from "../../../components/cards/hooks/useGridCardDimensions";
import { useAccountData } from "../../../hooks/useAccountData";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";
import {
  Section,
  Comic,
  Artist,
  Article,
  Release,
  ContentSectionCustomCard,
  LayoutStyle,
  Bundle,
} from "../../../types";

interface ExpandedContentGridProps {
  section: Section;
  data: (
    | Comic
    | Artist
    | Article
    | Release
    | ContentSectionCustomCard
    | Bundle
  )[];
  header: React.ReactNode;
  /** Callback for scroll restoration - reports first visible row index */
  onVisibleIndexChange?: (index: number) => void;
  /** Row index to restore scroll to on back navigation */
  restoreToIndex?: number;
}

export function ExpandedContentGrid({
  section,
  data,
  header,
  onVisibleIndexChange,
  restoreToIndex,
}: ExpandedContentGridProps) {
  const topBarHeight = useTopBarHeight();
  const accountData = useAccountData();
  const isSearchV2 = !!accountData?.features?.["search_v2"];

  // Create a mock page structure for ResultsGrid
  const mockPages = [
    {
      data: {
        payload: {
          results: data,
          count: data.length,
          next: null,
          previous: null,
        },
      },
    },
  ];

  // Adjust card sizes based on original layout
  const originalContext = section.meta.layout.style.context;
  const originalLayout = section.meta.layout;
  const originalMode = originalLayout.style.mode;
  const originalSize = originalLayout.style.size;

  // V2: use FluidVirtualGrid with v2 card styles
  if (isSearchV2 && originalContext !== "bundle") {
    const v2Layout = {
      ...originalLayout,
      style: {
        ...originalLayout.style,
        mode: "grid" as LayoutStyle["mode"],
        size: "v2" as LayoutStyle["size"],
      },
    };
    const dims = sizeEstimator(
      v2Layout.style.context,
      v2Layout.style.mode,
      v2Layout.style.size,
    );
    return (
      <FluidVirtualGrid
        gridKey={`v2-expanded-${section.id}`}
        header={header}
        gridHeightAdjustment={`${topBarHeight.get()}px`}
        layout={v2Layout}
        minCardWidth={dims.width}
        gap={24}
        isLoading={false}
        hasNextPage={false}
        allPages={mockPages as any}
        onVisibleIndexChange={onVisibleIndexChange}
        restoreToIndex={restoreToIndex}
      />
    );
  }

  // V1: existing logic
  let adjustedLayout = { ...originalLayout };
  const isReleaseListCard =
    originalMode === "horizontal" && originalSize === "r_list";

  if (
    originalMode === "list" ||
    (originalMode === "horizontal" && originalSize === "c_1") ||
    isReleaseListCard
  ) {
    adjustedLayout = {
      ...originalLayout,
      style: {
        ...originalLayout.style,
        mode: "list" as LayoutStyle["mode"],
        size: isReleaseListCard ? "r_list" : "c_1",
      },
    };
  } else if (originalMode === "horizontal" || originalMode === "grid") {
    adjustedLayout = {
      ...originalLayout,
      style: {
        ...originalLayout.style,
        mode: "grid" as LayoutStyle["mode"],
        size: "l",
      },
    };
  }

  if (originalContext === "bundle") {
    return (
      <BundleResultsGrid
        header={header}
        gridHeightAdjustment={`${topBarHeight.get()}px`}
        layout={adjustedLayout}
        isLoading={false}
        hasNextPage={false}
        allPages={mockPages as any}
        columnPadding={16}
        gridKey={"bundle-expanded-content-grid"}
      />
    );
  }

  return (
    <ResultsGrid
      header={header}
      gridHeightAdjustment={`${topBarHeight.get()}px`}
      layout={adjustedLayout}
      isLoading={false}
      hasNextPage={false}
      allPages={mockPages as any}
      columnPadding={16}
      onVisibleIndexChange={onVisibleIndexChange}
      restoreToIndex={restoreToIndex}
    />
  );
}
